import React from 'react';
import {
    CAR_ROW_STATUS_OPTIONS,
    computeCarDisplayStatus,
} from './carReportUtils';
import {
    DEFAULT_D4_WHYS,
    formatDateOnly,
    normalizeD1Rows,
    normalizeD3Rows,
    normalizeD6Rows,
    parseD4Whys,
} from './eightDReportUtils';
import { ReportNonImageFileList, ReportPhotoGrid } from './qValueReportMedia';

function displayDate(value) {
    const d = formatDateOnly(value);
    if (!d) return '';
    const [y, m, day] = d.split('-');
    return `${day}/${m}/${y}`;
}

function cell(val) {
    return val || '\u00a0';
}

function filterRows(rows, keys) {
    return (rows || []).filter((row) => keys.some((k) => String(row[k] || '').trim()));
}

function PrintPage({ pageIndex, children }) {
    const style = pageIndex > 0 ? { pageBreakBefore: 'always', breakBefore: 'page' } : undefined;
    return <div className="eight-d-print-page" style={style}>{children}</div>;
}

export default function EightDReportPrintView({ data }) {
    if (!data) return null;

    const displayStatus = data.displayStatus || computeCarDisplayStatus(
        data.status,
        data.dateClosed || data.d6ValidationDate,
        data.targetDate || data.d6TargetDate
    );

    const d1Rows = filterRows(normalizeD1Rows(data.d1TeamMembers), ['role', 'name', 'department', 'contactEmail']);
    const d3Rows = filterRows(normalizeD3Rows(data.d3ContainmentRows), ['actionDescription', 'owner']);
    const d6Rows = normalizeD6Rows(data.d6PcaRows).filter((row) => {
        const keys = ['pcaActionItem', 'owner', 'targetDate', 'validationDate', 'validationMethodResult'];
        const hasText = keys.some((k) => String(row[k] || '').trim());
        const hasPhotos = (row.validationPhotos || []).length > 0;
        return hasText || hasPhotos;
    });
    const d4 = parseD4Whys(data.d4Whys, data.d4_root_cause);
    const attachments = data.d7Attachments || [];

    const pad = (rows, min) => {
        const list = [...rows];
        while (list.length < min) list.push({});
        return list;
    };

    return (
        <div className="eight-d-print">
            <PrintPage pageIndex={0}>
                <div className="banner">EIGHT DISCIPLINES (8D) PROBLEM SOLVING REPORT</div>
                <table className="grid">
                    <tbody>
                        <tr>
                            <td style={{ width: '18%', fontWeight: 600 }}>Report ID</td>
                            <td style={{ width: '32%' }}>{cell(data.reportNo)}</td>
                            <td style={{ width: '18%', fontWeight: 600 }}>Date Opened</td>
                            <td>{displayDate(data.dateOpened)}</td>
                        </tr>
                        <tr>
                            <td style={{ fontWeight: 600 }}>Origin / Source</td>
                            <td>{cell(data.originSource)}</td>
                            <td style={{ fontWeight: 600 }}>Revision</td>
                            <td>{cell(data.revisionLevel)}</td>
                        </tr>
                        <tr>
                            <td style={{ fontWeight: 600 }}>Customer Name</td>
                            <td>{cell(data.customerName)}</td>
                            <td style={{ fontWeight: 600 }}>Part Number</td>
                            <td>{cell(data.partNo)}</td>
                        </tr>
                        <tr>
                            <td style={{ fontWeight: 600 }}>Part Description</td>
                            <td>{cell(data.partDescription)}</td>
                            <td style={{ fontWeight: 600 }}>Status</td>
                            <td>{cell(displayStatus)}</td>
                        </tr>
                        <tr>
                            <td style={{ fontWeight: 600 }}>Owner</td>
                            <td>{cell(data.ownerName)}</td>
                            <td style={{ fontWeight: 600 }}>Lead Engineer</td>
                            <td>{cell(data.leadEngineer)}</td>
                        </tr>
                        <tr>
                            <td style={{ fontWeight: 600 }}>Machine / Line</td>
                            <td colSpan={3}>{cell(data.machineLine)}</td>
                        </tr>
                    </tbody>
                </table>

                <div className="section">D1 — Establish the Team</div>
                <table className="grid">
                    <thead>
                        <tr className="head">
                            <th>Role</th><th>Name</th><th>Department</th><th>Contact / Email</th>
                        </tr>
                    </thead>
                    <tbody>
                        {pad(d1Rows, 4).map((r, i) => (
                            <tr key={`d1-${i}`}>
                                <td>{cell(r.role)}</td><td>{cell(r.name)}</td>
                                <td>{cell(r.department)}</td><td>{cell(r.contactEmail)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <div className="section">D2 — Describe the Problem (5W2H Approach)</div>
                <div className="hint">
                    What is the problem? Who identified it? Where was it found? When did it happen? Why is it a problem?
                    How many items are affected? How frequently does it occur?
                </div>
                <div style={{ border: '1px solid #6c757d', minHeight: '56px', padding: '6px', whiteSpace: 'pre-wrap' }}>
                    {cell(data.d2_problem || data.problemDescription)}
                </div>
                {(data.d2ProblemPhotos || []).length > 0 && (
                    <>
                        <div style={{ fontWeight: 600, fontSize: '10px', margin: '8px 0 4px' }}>Problem photos</div>
                        <ReportPhotoGrid items={data.d2ProblemPhotos} />
                    </>
                )}
                <div className="section">D3 — Interim Containment Actions</div>
                <table className="grid">
                    <thead>
                        <tr className="head">
                            <th>Action Description</th><th>Owner</th><th>Target Date</th>
                            <th>Impl. Date</th><th>Effectiveness %</th><th>Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {pad(d3Rows, 2).map((r, i) => (
                            <tr key={`d3-${i}`}>
                                <td>{cell(r.actionDescription)}</td><td>{cell(r.owner)}</td>
                                <td>{displayDate(r.targetDate)}</td><td>{displayDate(r.implDate)}</td>
                                <td>{cell(r.effectivenessPct)}</td><td>{cell(r.status)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <div className="section">D4 — Root Cause Analysis (5 Whys / Escape Point)</div>
                <table className="grid">
                    <tbody>
                        {[
                            ['Why did the issue occur?', d4.why1],
                            ['Why did that happen?', d4.why2],
                            ['Why...?', d4.why3],
                            ['Why...?', d4.why4],
                            ['Why...?', d4.why5],
                        ].map(([label, val], i) => (
                            <tr key={`w-${i}`}>
                                <td style={{ width: '34%', fontWeight: 600 }}>{label}</td>
                                <td style={{ whiteSpace: 'pre-wrap' }}>{cell(val)}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </PrintPage>

            <PrintPage pageIndex={1}>
                <div className="section">D5 — Choose &amp; Verify Permanent Corrective Actions (PCA)</div>
                <div className="hint">
                    Describe the chosen permanent solutions and how they were tested or simulated to ensure they work before full deployment.
                </div>
                <div style={{ border: '1px solid #6c757d', minHeight: '48px', padding: '6px', whiteSpace: 'pre-wrap', marginBottom: '8px' }}>
                    {cell(data.d5_corrective_action)}
                </div>

                <div className="section">D6 — Implement Permanent Corrective Actions</div>
                <table className="grid">
                    <thead>
                        <tr className="head">
                            <th>PCA Action Item</th><th>Owner</th><th>Target Date</th>
                            <th>Validation Date</th><th>Validation Method &amp; Result</th>
                        </tr>
                    </thead>
                    <tbody>
                        {pad(d6Rows, 2).map((r, i) => (
                            <tr key={`d6-${i}`}>
                                <td>{cell(r.pcaActionItem)}</td><td>{cell(r.owner)}</td>
                                <td>{displayDate(r.targetDate)}</td>
                                <td>{displayDate(r.validationDate)}</td>
                                <td>
                                    <div style={{ whiteSpace: 'pre-wrap' }}>{cell(r.validationMethodResult)}</div>
                                    {(r.validationPhotos || []).length > 0 && (
                                        <ReportPhotoGrid items={r.validationPhotos} />
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <div className="section">D7 — Prevent Recurrence (Systemic Changes)</div>
                <div className="hint">
                    What documents, Control Plans, FMEAs, or Standard Operating Procedures (SOPs) must be updated to prevent this across similar lines or future products?
                </div>
                <div style={{ border: '1px solid #6c757d', minHeight: '48px', padding: '6px', whiteSpace: 'pre-wrap' }}>
                    {cell(data.d7_prevent_recurrence)}
                </div>
                {attachments.length > 0 && (
                    <>
                        <div style={{ fontWeight: 600, fontSize: '10px', margin: '8px 0 4px' }}>Attachments (photos)</div>
                        <ReportPhotoGrid items={attachments} />
                        <ReportNonImageFileList items={attachments} />
                    </>
                )}

                <div className="section">D8 — Recognize &amp; Congratulate the Team</div>
                <div style={{ border: '1px solid #6c757d', minHeight: '36px', padding: '6px', whiteSpace: 'pre-wrap' }}>
                    {cell(data.d8_team_recognition)}
                </div>

                <div className="section">Submit for Approval</div>
                <table className="grid">
                    <thead><tr className="head"><th>Status</th></tr></thead>
                    <tbody>
                        <tr>
                            <td>
                                {CAR_ROW_STATUS_OPTIONS.map((opt) => (
                                    <div key={opt}>{displayStatus === opt ? '☑' : '☐'} {opt}</div>
                                ))}
                                {displayDate(data.d6ValidationDate || data.dateClosed) && (
                                    <div style={{ marginTop: '6px', fontSize: '9px' }}>
                                        Validation / Closed: {displayDate(data.d6ValidationDate || data.dateClosed)}
                                    </div>
                                )}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </PrintPage>
        </div>
    );
}
